import Phaser from 'phaser';
import { gameConfig } from './config';
import { getTextRes, computeMetrics } from './dpr.js';
import { CheatUnlocker, CheatConsole, CheatCommands } from './cheat/index.js';
import { GameStateManager } from './state/GameStateManager.js';
import { ServerHealthService } from './services/ServerHealthService.js';
import { AuthService } from './services/AuthService.js';
import { LogCollector } from './services/LogCollector.js';
import { SocketService } from './services/SocketService.js';
import { SyncService } from './services/SyncService.js';
import { FEATURE_MULTIPLAYER } from './featureFlags.js';
import { setupVersionedFetch } from './version/fetchWrapper.js';
import { showUpdateRequiredOverlay } from './version/updateRequiredOverlay.js';
import { startVersionPolling } from './version/versionPoller.js';
import { mountResetOverlay } from './passwordReset/resetOverlay.js';

// Initialize log collector early so uncaught errors are captured from startup
LogCollector.getInstance();

/**
 * Pause all active Phaser scenes (except boot/login which have no meaningful
 * paused state) and force-flush any pending debounced save. Called via the
 * overlay's `beforeMount` hook so a player mid-BattleScene doesn't lose
 * progress when they click "Recharger": the game is frozen behind the
 * overlay AND their latest state has been pushed to the server with
 * `keepalive: true` before the modal even appears.
 */
function pauseAndFlush(): void {
  try {
    game.scene.getScenes(true).forEach((s) => {
      const key = s.sys.settings.key;
      if (key === 'BootScene' || key === 'LoginScene') return;
      game.scene.pause(key);
    });
  } catch {
    // Phaser not ready yet (overlay fired before game.ready) — nothing to pause.
  }
  SyncService.getInstance().flushPendingSave();
}

// Global fetch wrapper: injects X-Client-Version on all requests, shows a
// blocking "Mise à jour obligatoire" overlay on 426 (no silent reload loop
// when the web bundle is stale — the user clicks "Recharger" themselves).
declare const __APP_VERSION__: string;
setupVersionedFetch(__APP_VERSION__, () => {
  showUpdateRequiredOverlay(pauseAndFlush);
});

// Proactive version poll — every 60 s, fetch /api/version and compare the
// live web bundle version against the bundled __APP_VERSION__. Mismatch
// triggers the same overlay even when the player isn't making API calls
// (so an idle menu / mid-fight session still gets bumped onto the new
// build instead of waiting for the next save round-trip).
startVersionPolling(__APP_VERSION__, () => {
  showUpdateRequiredOverlay(pauseAndFlush);
});

// Force text resolution = 2 on every this.add.text() call
const _origText = Phaser.GameObjects.GameObjectFactory.prototype.text;
Phaser.GameObjects.GameObjectFactory.prototype.text = function (...args: Parameters<typeof _origText>) {
  const t = _origText.apply(this, args);
  t.setResolution(2);
  return t;
};

const game = new Phaser.Game(gameConfig);

// Wire the InviteToast singleton with the live Phaser.Game so it can find
// an active scene at render time even when the user is on a scene that
// didn't explicitly call `bindInviteToast` (HeroScene, MerchantScene, etc.).
// Also register the fallback invite handler so MultiplayerService routes
// incoming invites to the toast singleton when no scene-bound handler exists.
// Without this wiring, invites arriving on those scenes were silently dropped.
// Gated behind FEATURE_MULTIPLAYER so the Steam build (flag = false) doesn't
// pull MultiplayerService into the bundle at all (tree-shaken).
if (FEATURE_MULTIPLAYER) {
  Promise.all([
    import('./ui/InviteToast.js'),
    import('./services/MultiplayerService.js'),
  ]).then(([{ InviteToast }, { MultiplayerService }]) => {
    const toast = InviteToast.getInstance();
    toast.setGame(game);
    MultiplayerService.getInstance().setFallbackInviteHandler((p) => toast.enqueue(p));
  }).catch((e) => console.warn('[invite-toast] wiring failed', e));
}

// Suppress the browser's right-click context menu on the game canvas so
// right-click is a game action only (no Inspect / Back / Copy menu popping
// up mid-combat). We attach once the canvas exists; it sits inside the
// Phaser parent div, so we listen on that div too for belt-and-suspenders.
game.events.once('ready', () => {
  const canvas: HTMLCanvasElement | null = game.canvas;
  const handler = (ev: MouseEvent) => ev.preventDefault();
  canvas?.addEventListener('contextmenu', handler);
  canvas?.parentElement?.addEventListener('contextmenu', handler);
});

// Canvas = CSS size exactly → 1:1 pixel mapping, no browser interpolation.
// `game.scale.resize` updates the canvas internal dimensions and emits the
// `resize` event each scene's `setupCamera` listens to (so cameras pick up
// the new zoom). `game.scale.refresh()` is called explicitly afterwards
// because under `Scale.NONE` the input plugin caches the canvas bounding
// rect → without refresh, click coordinates can drift after a viewport
// change (notably F11 fullscreen, where the user previously had to reload
// before hit-areas would line up again).
function resizeCanvas() {
  const m = computeMetrics(window.innerWidth, window.innerHeight);
  game.scale.resize(m.canvasW, m.canvasH);
  const c = game.canvas;
  c.style.position = 'absolute';
  c.style.width = m.cssW + 'px';
  c.style.height = m.cssH + 'px';
  c.style.left = m.cssLeft + 'px';
  c.style.top = m.cssTop + 'px';
  // Force the input system to re-read the canvas bounding rect.
  game.scale.refresh();
}
game.events.once('ready', resizeCanvas);
window.addEventListener('resize', resizeCanvas);

// F11 / Fullscreen API hit-area drift fix. Browser fullscreen transitions
// can fire `resize` before the canvas's bounding rect has settled (the
// browser is still animating the chrome away), so the input plugin captures
// intermediate dimensions and clicks land in the wrong place until the
// next reload. Mitigation: hook `fullscreenchange` (JS Fullscreen API path)
// AND defer two extra `resizeCanvas` calls — one on the next frame, one a
// short delay later — so we re-read the bounding rect after the browser
// has finished settling. Cheap belt-and-suspenders, fixes the "reload
// after F11" pain.
const refreshAfterTransition = () => {
  resizeCanvas();
  requestAnimationFrame(resizeCanvas);
  setTimeout(resizeCanvas, 250);
};
document.addEventListener('fullscreenchange', refreshAfterTransition);
// Also trigger on the visual viewport resize event when supported — some
// browsers fire this earlier than `window.resize` during fullscreen
// transitions, and it captures the post-settle dimensions.
const _vv = window.visualViewport;
if (_vv) _vv.addEventListener('resize', resizeCanvas);

// Keyboard Lock — capture ESC while in fullscreen so the browser doesn't
// exit fullscreen on every Escape press. ESC is bound in-game (closes
// panels / cancels selections), and without this lock every in-game ESC
// silently dropped the player out of fullscreen. Chromium-only API
// (Chrome / Edge / Opera) — Firefox + Safari fall back to default browser
// behavior. Phaser's keyboard input still receives the ESC keydown via
// the document keydown event, so in-game handlers continue to fire.
const _kb = (navigator as { keyboard?: { lock?: (keys: string[]) => Promise<void>; unlock?: () => void } }).keyboard;
const syncEscapeKeyboardLock = (): void => {
  if (!_kb?.lock || !_kb?.unlock) return;
  if (document.fullscreenElement) {
    _kb.lock(['Escape']).catch(() => { /* lock denied / unsupported — fall back */ });
  } else {
    _kb.unlock();
  }
};
document.addEventListener('fullscreenchange', syncEscapeKeyboardLock);

// Auth-driven save/state cleanup — fires in BOTH web AND Steam builds. The
// previous version coupled this to the socket lifecycle (gated below) which
// meant the Steam build (FEATURE_MULTIPLAYER=false) skipped these cleanups
// entirely → User A's pending saves could flush under User B's token after a
// re-login. Separated so the cleanup runs regardless of MP wiring.
const auth = AuthService.getInstance();
auth.onLogout(() => {
  // Drop any pending debounced save so User A's state doesn't get flushed under User B's token.
  SyncService.getInstance().clearPending();
  // Wipe in-memory game state so the next login starts from a clean slate.
  GameStateManager.getInstance().resetForLogout();
});

// Socket.IO — connect when logged in, handle admin kick and forced updates.
// Gated behind FEATURE_MULTIPLAYER so the Steam build doesn't open a
// websocket connection at all. ONLY socket-specific lifecycle lives here;
// auth state/save cleanup above runs in both builds.
if (FEATURE_MULTIPLAYER) {
  const socketSvc = SocketService.getInstance();

  // force-update: desktop delegates to main process via preload IPC (version gate
  // window), web shows the blocking "Mise à jour obligatoire" overlay.
  const _w = window as Window & { roeDesktop?: { triggerForceUpdate?: () => void } };
  socketSvc.setForceUpdateHandler(() => {
    if (_w.roeDesktop?.triggerForceUpdate) {
      _w.roeDesktop.triggerForceUpdate();
    } else {
      showUpdateRequiredOverlay(pauseAndFlush);
    }
  });

  socketSvc.setForceLogoutHandler((reason?: string) => {
    const active = game.scene.getScenes(true).map(s => s.sys.settings.key);
    active.forEach(key => game.scene.stop(key));
    game.scene.start('LoginScene', { kickReason: reason });
  });
  if (auth.isLoggedIn()) socketSvc.connect();
  auth.onLogin(() => socketSvc.connect());
  auth.onLogout(() => socketSvc.disconnect());
}

// Start server health monitoring — shows OfflineScene when unreachable, auto-returns on recovery
ServerHealthService.getInstance().start({
  onOffline: () => {
    const active = game.scene.getScenes(true).map(s => s.sys.settings.key).filter(k => k !== 'OfflineScene');
    active.forEach(key => game.scene.stop(key));
    game.scene.start('OfflineScene');
  },
  onOnline: () => { /* OfflineScene handles its own recovery navigation */ },
});

// Cheat system — type "roedev" to unlock, then backtick (`) to toggle console
let cheatConsole: CheatConsole | null = null;
new CheatUnlocker(() => {
  const gsm = GameStateManager.getInstance();
  const commands = new CheatCommands(gsm, game as any);
  cheatConsole = new CheatConsole(game as any, async (cmd) => {
    const result = await commands.execute(cmd);
    cheatConsole?.log(result);
  });
});

// Keep game and audio running when the browser tab/window loses focus
game.sound.pauseOnBlur = false;
game.events.on('hidden', () => game.loop.wake());
game.events.on('visible', () => game.loop.wake());

// Password reset deep-link: when the URL has `?reset=<token>`, mount the
// reset overlay on top of the Phaser canvas. On success the overlay scrubs
// the token from the URL and triggers LoginScene with a success banner.
const resetToken = new URLSearchParams(window.location.search).get('reset');
if (resetToken) {
  mountResetOverlay(resetToken, () => {
    // Restart LoginScene so the success banner renders cleanly.
    try {
      game.scene.stop('LoginScene');
    } catch { /* may not be started yet */ }
    game.scene.start('LoginScene', { resetBanner: 'success' });
  });
}

export default game;
